import { AlertTriangle } from "lucide-react";
import type { FlowCostEstimate } from "@zworks/console-contracts";
import { Input } from "@/components/ui/input";

export function FlowCostBar({
  estimate,
  workflowCap,
  canEdit,
  onCapChange,
}: {
  estimate: FlowCostEstimate;
  workflowCap: number | null;
  canEdit: boolean;
  onCapChange: (cap: number | null) => void;
}) {
  return (
    <div className="shrink-0 rounded-xl border border-border/70 bg-card/75 px-3 py-2 shadow-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="text-xs text-muted-foreground" data-testid="cost-estimate">
          Estimated{" "}
          <span className="font-semibold text-foreground">
            {estimate.minCredits}–{estimate.maxCredits} credits
          </span>
        </p>
        <p className="text-xs text-muted-foreground" data-testid="workflow-cap">
          Estimated cap{" "}
          <span className="font-semibold text-foreground">{estimate.workflowCap} credits</span>
        </p>
        <label className="flex flex-col gap-1 text-xs font-medium">
          <span className="flex items-center gap-2">
            Workflow cap
            <Input
              type="number"
              min={1}
              step={1}
              value={workflowCap ?? ""}
              disabled={!canEdit}
              placeholder="No cap"
              aria-label="Workflow cap in credits"
              onChange={(event) => {
                const raw = event.target.value;
                if (raw === "") {
                  onCapChange(null);
                  return;
                }
                const value = Number.parseInt(raw, 10);
                if (!Number.isFinite(value) || value < 1) {
                  return;
                }
                onCapChange(value);
              }}
              className="h-8 w-28"
            />
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            Saved with the draft and reserved when a run starts.
          </span>
        </label>
      </div>
      {estimate.isLowCap && estimate.warning ? (
        <p
          role="alert"
          className="mt-1.5 flex items-center gap-1.5 rounded-md border border-danger/30 bg-danger/10 px-2 py-1 text-xs font-medium text-danger"
        >
          <AlertTriangle className="size-3.5" aria-hidden="true" />
          <span>{estimate.warning}</span>
        </p>
      ) : null}
    </div>
  );
}
